import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { X, PaperPlaneTilt, LinkSimple, Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Point 6 — Send DWS ID / Player ID credentials straight into the customer's
// chat. The admin/handler picks from the admin-configured links (same list the
// customer sees under Groups → Links) and/or adds a custom link, edits the
// English/Hindi mix message, then sends. Backend inserts it into the
// customer's most-recent active chat room.
const buildDefaultMessage = (kind, creds) => {
  const dws = `🔐 *DWS Login Details*\n\nDWS ID: ${creds.username || '-'}\nMobile Number: ${creds.mobile_number || '-'}\nPassword: ${creds.password || '(unchanged)'}`;
  const player = `🎮 *Player ID Details*\n\nPlayer ID: ${creds.player_id || '-'}\nPassword: ${creds.player_password || '-'}`;
  if (kind === 'player') {
    return `${player}\n\nYe aapki Player ID aur password hai. Neeche diye link par jaakar khelein 👇`;
  }
  if (kind === 'both') {
    return `Here is your DWS ID credential:\n${dws}\n\nHere is your Player ID:\n${player}\n\nDWS ID se aap apni DWS app me login karein. Neeche diye link par jaakar login/khelein 👇`;
  }
  return `${dws}\n\nIs DWS ID aur password se aap apni DWS app me login kar sakte hain.\nNeeche diye link par jaakar login karein 👇`;
};

const SendCredentialsModal = ({ client, userId, kind, credentials, defaultLink = '', onClose }) => {
  const [message, setMessage] = useState(() => buildDefaultMessage(kind, credentials || {}));
  const [links, setLinks] = useState([]);
  const [selected, setSelected] = useState({}); // id -> bool
  const [customDesc, setCustomDesc] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  // Point 8(iii) — DWS ID / Both default to the real customer login link.
  const [customLinks, setCustomLinks] = useState(() =>
    (defaultLink && (kind === 'dwsid' || kind === 'both')) ? [{ description: 'Login', url: defaultLink }] : []
  );
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/links`);
        setLinks(Array.isArray(data) ? data : []);
      } catch (_) { setLinks([]); }
    })();
  }, []);

  const chosenLinks = useMemo(() => {
    const fromAdmin = links.filter((l) => selected[l.id]).map((l) => ({ description: l.description, url: l.url }));
    return [...fromAdmin, ...customLinks];
  }, [links, selected, customLinks]);

  const finalText = useMemo(() => {
    if (!chosenLinks.length) return message;
    const linkLines = chosenLinks.map((l) => `${l.description ? l.description + ': ' : ''}${l.url}`).join('\n');
    return `${message}\n\n${linkLines}`;
  }, [message, chosenLinks]);

  const addCustom = () => {
    const url = customUrl.trim();
    if (!url) { toast.error('Enter a link URL'); return; }
    setCustomLinks((p) => [...p, { description: customDesc.trim(), url }]);
    setCustomDesc(''); setCustomUrl('');
  };

  const handleSend = async () => {
    if (!finalText.trim()) { toast.error('Message is empty'); return; }
    try {
      setSending(true);
      const res = await axios.post(`${API}/clients/${client.id}/send-credentials`, {
        actor_id: userId,
        kind,
        text: finalText,
      });
      toast.success(`Credentials sent to ${client.username || 'customer'} chat!`);
      setSending(false);
      onClose && onClose(res.data);
    } catch (err) {
      setSending(false);
      toast.error(err.response?.data?.detail || 'Failed to send credentials');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="send-credentials-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#128C7E] text-white p-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-base font-bold flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" />
            Send {kind === 'player' ? 'Player ID' : kind === 'both' ? 'DWS ID + Player ID' : 'DWS ID'} to Chat
          </h2>
          <button type="button" onClick={() => onClose && onClose()} className="p-1 hover:bg-white/20 rounded-full" data-testid="send-cred-close">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Editable message */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={7}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
              data-testid="send-cred-message"
            />
          </div>

          {/* Admin links picker */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
              <LinkSimple size={14} weight="bold" /> Attach Links (Admin links)
            </label>
            {links.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No admin links configured yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {links.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer" data-testid={`send-cred-link-${l.id}`}>
                    <input
                      type="checkbox"
                      checked={!!selected[l.id]}
                      onChange={(e) => setSelected((p) => ({ ...p, [l.id]: e.target.checked }))}
                      className="w-4 h-4 accent-emerald-600"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 truncate">{l.description || 'Link'}</span>
                      <span className="block text-[11px] text-gray-500 truncate">{l.url}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Custom link */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Add Custom Link</label>
            <div className="flex gap-2">
              <input
                value={customDesc}
                onChange={(e) => setCustomDesc(e.target.value)}
                placeholder="Label (optional)"
                className="w-1/3 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="send-cred-custom-desc"
              />
              <input
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="send-cred-custom-url"
              />
              <button type="button" onClick={addCustom} className="px-3 bg-gray-800 text-white rounded-lg" data-testid="send-cred-add-custom">
                <Plus size={16} weight="bold" />
              </button>
            </div>
            {customLinks.length > 0 && (
              <div className="mt-2 space-y-1">
                {customLinks.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                    <span className="flex-1 truncate">{l.description ? l.description + ': ' : ''}{l.url}</span>
                    <button type="button" onClick={() => setCustomLinks((p) => p.filter((_, j) => j !== i))} className="text-red-500">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="bg-[#DCF8C6] rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap break-words" data-testid="send-cred-preview">
            {finalText}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 p-4 flex gap-3 border-t rounded-b-xl">
          <button type="button" onClick={() => onClose && onClose()} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            data-testid="send-cred-send-btn"
          >
            <PaperPlaneTilt size={18} weight="bold" />
            {sending ? 'Sending...' : 'Send to Chat'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendCredentialsModal;
